import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatError, MatFormField, MatLabel } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatRadioButton, MatRadioGroup } from "@angular/material/radio";
import { TranslatePipe } from "@ngx-translate/core";
import { filter, map, take } from "rxjs";

import { DocumentDetails } from "@app/constants/document-mappings";
import { DOCUMENT_TYPE, ON_BEHALF_TYPE } from "@app/models/registration-form";
import { AddressSectionComponent } from "@app/modules/shared/address-section/address-section.component";
import { ContactSectionComponent } from "@app/modules/shared/contact-section/contact-section.component";
import { SharedModule } from "@app/modules/shared/shared.module";
import { AppStateService } from "@app/services/app-state.service";
import { RouteService } from "@app/services/route.service";
import { RequiredSuffixDirective } from "@app/shared/directives/required-suffix.directive";
import { CastToFormControlPipe } from "@app/shared/pipes/cast-to-form-control.pipe";
import { CastToFormGroupPipe } from "@app/shared/pipes/cast-to-form-group.pipe";
import { NextButtonDisabledPipe } from "@app/shared/pipes/next-button-disabled.pipe";
import { CustomValidators } from "@app/shared/validators/custom-validators";

import { CommonStepsModule } from "../common-steps.module";
@Component({
  selector: "app-contact",
  standalone: true,
  imports: [
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    MatRadioButton,
    MatRadioGroup,
    NextButtonDisabledPipe,
    SharedModule,
    TranslatePipe,
    RequiredSuffixDirective,
    CommonStepsModule,
    ContactSectionComponent,
    AddressSectionComponent,
    CastToFormControlPipe,
    CastToFormGroupPipe,
  ],
  templateUrl: "./contact.component.html",
})
export class ContactComponent implements OnInit {
  readonly #appStateService: AppStateService = inject(AppStateService);
  readonly #formBuilder: FormBuilder = inject(FormBuilder);
  readonly #routeService: RouteService = inject(RouteService);
  readonly #destroyRef = inject(DestroyRef);

  public contactForm!: FormGroup;
  public allTouched = false;
  public powerOfAttorneyDocumentDetails: DocumentDetails = {
    typeName: DOCUMENT_TYPE.POWER_OF_ATTORNEY,
  };
  public roleTypes = Object.keys(ON_BEHALF_TYPE);
  public roles = ON_BEHALF_TYPE;

  public ngOnInit(): void {
    this.#createContactForm();
    this.#updateFormFromState();
    this.#watchForm();
  }

  #createContactForm(): void {
    this.contactForm = this.#formBuilder.group({
      basicData: this.#formBuilder.group(
        {
          firstName: [
            null,
            [
              CustomValidators.trimValidator,
              CustomValidators.shortText,
              Validators.required,
            ],
          ],
          lastName: [
            null,
            [
              CustomValidators.trimValidator,
              CustomValidators.shortText,
              Validators.required,
            ],
          ],
          email: [
            null,
            [
              CustomValidators.trimValidator,
              CustomValidators.shortText,
              Validators.required,
              CustomValidators.email,
            ],
          ],
          phone: [
            null,
            [
              CustomValidators.trimValidator,
              CustomValidators.shortText,
              CustomValidators.phoneNumberValidator(),
              Validators.required,
            ],
          ],
        },
        {
          updateOn: "blur",
        },
      ),
      hasDifferentAddress: [null, Validators.required],
      differentAddress: this.#formBuilder.group(
        {
          zipCode: [
            null,
            [
              CustomValidators.trimValidator,
              CustomValidators.shortText,
              Validators.required,
              CustomValidators.germanZip,
            ],
          ],
          city: [
            null,
            [
              CustomValidators.trimValidator,
              CustomValidators.shortText,
              Validators.required,
            ],
          ],
          street: [
            null,
            [
              CustomValidators.trimValidator,
              CustomValidators.shortText,
              Validators.required,
            ],
          ],
          streetNumber: [
            null,
            [
              CustomValidators.trimValidator,
              CustomValidators.shortText,
              Validators.required,
            ],
          ],
        },
        {
          updateOn: "blur",
        },
      ),
      role: [null, Validators.required],
      // needed when role is not CONNECTION_HOLDER
      onBehalfContactDetails: this.#formBuilder.group({
        basicData: this.#formBuilder.group(
          {
            firstName: [
              null,
              [
                CustomValidators.trimValidator,
                CustomValidators.shortText,
                Validators.required,
              ],
            ],
            lastName: [
              null,
              [
                CustomValidators.trimValidator,
                CustomValidators.shortText,
                Validators.required,
              ],
            ],
            email: [
              null,
              [
                CustomValidators.trimValidator,
                CustomValidators.shortText,
                CustomValidators.email,
                Validators.required,
              ],
            ],
            phone: [
              null,
              [
                CustomValidators.trimValidator,
                CustomValidators.shortText,
                CustomValidators.phoneNumberValidator(),
                Validators.required,
              ],
            ],
          },
          {
            updateOn: "blur",
          },
        ),
        address: this.#formBuilder.group(
          {
            zipCode: [
              null,
              [
                CustomValidators.trimValidator,
                CustomValidators.shortText,
                Validators.required,
                CustomValidators.germanZip,
              ],
            ],
            city: [
              null,
              [
                CustomValidators.trimValidator,
                CustomValidators.shortText,
                Validators.required,
              ],
            ],
            street: [
              null,
              [
                CustomValidators.trimValidator,
                CustomValidators.shortText,
                Validators.required,
              ],
            ],
            streetNumber: [
              null,
              [
                CustomValidators.trimValidator,
                CustomValidators.shortText,
                Validators.required,
              ],
            ],
          },
          {
            updateOn: "blur",
          },
        ),
        powerOfAttorney: [null, Validators.requiredTrue],
        powerOfAttorneyDocument: [null],
      }),
      // needed when role is INSTALLER
      installerDetails: this.#formBuilder.group({
        gridOperator: [
          null,
          [
            CustomValidators.trimValidator,
            CustomValidators.shortText,
            Validators.required,
          ],
        ],
        registrationNumber: [
          null,
          [
            CustomValidators.trimValidator,
            CustomValidators.shortText,
            Validators.required,
          ],
        ],
        companyName: [
          null,
          [
            CustomValidators.trimValidator,
            CustomValidators.shortText,
            Validators.required,
          ],
        ],
      }),
      // needed when role is AUTHORIZED_REPRESENTATIVE
      authorizedRepresentativeRole: [
        null,
        [
          CustomValidators.trimValidator,
          CustomValidators.shortText,
          Validators.required,
        ],
      ],
    });
  }

  #updateFormFromState(): void {
    this.#appStateService
      .observeState()
      .pipe(
        map(({ formData }) => formData.contact),
        filter(Boolean),
        take(1),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((contactDetails) => {
        this.contactForm.patchValue(contactDetails);
        this.#evaluateDifferentAddress();
        this.#evaluateRole();
      });
  }

  #evaluateDifferentAddress(): void {
    if (this.contactForm.get("hasDifferentAddress")?.value) {
      this.contactForm.get("differentAddress")!.enable();
    } else {
      this.contactForm.get("differentAddress")!.disable();
    }
  }

  #evaluateRole(): void {
    const role: ON_BEHALF_TYPE = this.contactForm.get("role")!.value;
    if (role === ON_BEHALF_TYPE.CONNECTION_HOLDER) {
      this.contactForm.get("onBehalfContactDetails")!.disable();
    } else this.contactForm.get("onBehalfContactDetails")!.enable();

    if (role === ON_BEHALF_TYPE.AUTHORIZED_REPRESENTATIVE) {
      this.contactForm.get("authorizedRepresentativeRole")!.enable();
    } else this.contactForm.get("authorizedRepresentativeRole")!.disable();

    if (role === ON_BEHALF_TYPE.INSTALLER) {
      this.contactForm.get("installerDetails")!.enable();
    } else this.contactForm.get("installerDetails")!.disable();
  }

  #watchForm(): void {
    this.#watchDifferentAddress();
    this.#watchRole();
  }

  #watchDifferentAddress(): void {
    this.contactForm
      .get("hasDifferentAddress")!
      .valueChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.#evaluateDifferentAddress());
  }

  #watchRole(): void {
    this.contactForm
      .get("role")!
      .valueChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.#evaluateRole());
  }

  #updateState(): void {
    this.#appStateService.updateFormData({
      contact: this.contactForm.value,
    });
  }

  public previous(): void {
    this.#updateState();
    this.#routeService.navigateToPreviousStep();
  }

  public next(): void {
    if (this.contactForm.valid) {
      this.#updateState();
      this.#routeService.navigateToNextStep();
    } else {
      this.contactForm.markAllAsTouched();
      this.allTouched = true;
    }
  }
}
